export enum ErrorType {
	NotFound = 'not-found',
	BadRequest = 'bad-request',
	Conflict = 'conflict',
	Unauthorized = 'unauthorized',
	Forbidden = 'forbidden',
	ServerError = 'server-error',
	InvalidInput = 'invalid-input',
	Unknown = 'unknown'
}

export class ApplicationError extends Error {
	type: ErrorType
	code?: string
	inputErrors?: Record<string, string>

	constructor(arg: {
		type: ErrorType
		message: string
		code?: string
		inputErrors?: Record<string, string>
	}) {
		super(arg.message)
		this.type = arg.type
		this.code = arg.code
		this.inputErrors = arg.inputErrors
	}
}

export type PossibleAnswer = {
	id: number
	questionId: number
	content: string
	position: number
}

export type OnboardingQuestions = {
	id: number
	title: string
	content: string
	active: boolean
	possibleAnswers: PossibleAnswer[]
	position: number
}

export type News = {
	content: string
	description: string
	title: string
}

export type CurrentPlan = {
	id: string
	name: string
	description: string
}

export type Subscription = {
	id: string
	active: boolean
	currentPlan: CurrentPlan
	startDate: string
	endDate: string
}

export type UserInformation = {
	email: string
	firstName: string
	fullName: string
	lastName: string
	onboarded: boolean
	subscription: Subscription
	isAuthenticated: boolean
	wsSubjects: string[]
	userHandler: string
}

export type Product = {
	id: string
	name: string
}

export type Entitlement = {
	currencyType: string
	id: string
	product: Product
	unit: string
}

export type Item = {
	currencyType: string
	currencyUnit: string
	currencyValue: number
	entitlement?: Entitlement
	id: string
	quotas: number
}

export type Plan = {
	active: boolean
	description: string
	id: string
	listItems: Item[]
	name: string
	needPayNow: boolean
	total: number
	type: string
}

export type PlanAcquisition = {
	// subscription_id: The id of the new subscription of the user, only returned when the plan is free.
	subscriptionId?: string
	plan: Plan
	cartId: string
	// clientSecret: The client secret to be used to complete the payment using stripe UI elements.
	clientSecret: string
	paymentId: string
	// returnUrl: The URL to redirect the user after the payment is completed.
	returnUrl: string
}

export type OnboardingAnswers = {
	questionId: number
	questionOptionId: number
}

export type OnboardingAnswersPayload = {
	onboardingAnswers: OnboardingAnswers[]
	planId: string
}

// DApps types
export type DApp = {
	active: boolean
	createdAt: string
	id: string
	name: string
	version: string
	webIdeUrl: string
	projectId?: string
	description: string
}

export type ProjectOwner = {
	id: string
	email: string
	lastName: string
	firstName: string
}

// Project types

export type Project = {
	active?: boolean
	createdAt: string
	description: string
	id?: string
	name: string
	publicId: string
	owner?: ProjectOwner
	isCollaborator: boolean
}

export interface SmartContract {
	id: string
	name: string
	description: string
	version: string
	active: boolean
	projectId: string
	createdAt: string
	status: string
	webIdeUrl: string
}

export type ProjectV2 = {
	id: string
	publicId: string
	name: string
	description?: string
	active: boolean
	createdAt: string
	owner: ProjectOwner
	isCollaborator: boolean
	smartContracts: SmartContract[]
	color?: {
		default: string
		hover: string
	}
}

export type Role = {
	label: string
	value: string
}

export type Collaborator = {
	role: string
	status: string
	userHandler: string
}

export type ProjectDetails = Project & {
	dapps: DApp[]
	collaborators: Collaborator[]
}

export type Template = {
	createdAt: string
	description: string
	downloadCount: number
	id: string
	name: string
	subtitle: string
	updatedAt: string
	version: string
}

export type UseCase = {
	id: string
	name: string
	version: string
	subtitle: string
	description: string
	content: string
	createdAt: string
	updatedAt: string
}

export enum EVENT_TYPES {
	Notification = 'notification',
	EnvironmentReady = 'ENVIRONMENT_READY',
	PaymentCompleted = 'PAYMENT_COMPLETED'
}

export type EnvironmentReadyData = {
	workspaceURL: string
	timestamp: string
}

export type PaymentCompleted = {
	id: string
	type: string
	subscriptionId?: string
	paymentId: string
	paymentStatus: 'paid' | 'failed'
	createdAt: string
}

export type EventMessageDataTypes = EnvironmentReadyData

export type EventMessage = {
	data: PaymentCompleted | EnvironmentReadyData | unknown
	subject: string
	type: EVENT_TYPES
}

export type AttestationData = {
	token: string
	ttlMillis: number
}

export type Team = {
	id: string
	name: string
	createdAt: string
	active: boolean
}

export type TeamMember = {
	id: string
	userId: string
	role: string
	teamId: string
}
